/**
 * Comprehensive error handling utilities
 */

// Error codes and their user-friendly messages
export const ERROR_CODES = {
  // Network errors
  NETWORK_ERROR: {
    code: 'NETWORK_ERROR',
    title: 'Connection Error',
    message: 'Unable to connect to the server. Please check your internet connection.',
    suggestions: ['Check your internet connection', 'Try refreshing the page', 'Contact support if the issue persists']
  },
  TIMEOUT_ERROR: {
    code: 'TIMEOUT_ERROR',
    title: 'Request Timeout',
    message: 'The request took too long to complete.',
    suggestions: ['Try again', 'Check your internet connection', 'The server might be busy']
  },
  
  // Authentication errors
  INVALID_TOKEN: {
    code: 'INVALID_TOKEN',
    title: 'Authentication Error',
    message: 'Your session has expired. Please log in again.',
    suggestions: ['Log in again', 'Clear browser cache if the problem persists']
  },
  TOKEN_EXPIRED: {
    code: 'TOKEN_EXPIRED',
    title: 'Session Expired',
    message: 'Your session has expired. Please log in again.',
    suggestions: ['Log in again']
  },
  UNAUTHORIZED: {
    code: 'UNAUTHORIZED',
    title: 'Access Denied',
    message: 'You do not have permission to perform this action.',
    suggestions: ['Log in with appropriate permissions', 'Contact support if you believe this is an error']
  },
  
  // Wallet errors
  WALLET_NOT_CONNECTED: {
    code: 'WALLET_NOT_CONNECTED',
    title: 'Wallet Not Connected',
    message: 'Please connect your wallet to continue.',
    suggestions: ['Connect your wallet', 'Make sure your wallet extension is installed and unlocked']
  },
  WALLET_CONNECTION_FAILED: {
    code: 'WALLET_CONNECTION_FAILED',
    title: 'Wallet Connection Failed',
    message: 'Failed to connect to your wallet.',
    suggestions: ['Try connecting again', 'Make sure your wallet is unlocked', 'Refresh the page and try again']
  },
  INSUFFICIENT_BALANCE: {
    code: 'INSUFFICIENT_BALANCE',
    title: 'Insufficient Balance',
    message: 'You do not have enough funds to complete this transaction.',
    suggestions: ['Add more funds to your wallet', 'Reduce the transaction amount']
  },
  INSUFFICIENT_SOL: {
    code: 'INSUFFICIENT_SOL',
    title: 'Insufficient SOL',
    message: 'You need more SOL to pay for transaction fees.',
    suggestions: ['Add SOL to your wallet for transaction fees', 'Minimum 0.01 SOL recommended']
  },
  
  // Transaction errors
  TRANSACTION_FAILED: {
    code: 'TRANSACTION_FAILED',
    title: 'Transaction Failed',
    message: 'The blockchain transaction failed to complete.',
    suggestions: ['Try again with higher gas fees', 'Check your wallet balance', 'Wait a moment and retry']
  },
  TRANSACTION_TIMEOUT: {
    code: 'TRANSACTION_TIMEOUT',
    title: 'Transaction Timeout',
    message: 'The transaction took too long to confirm.',
    suggestions: ['Check the transaction status on Solscan', 'Try again if the transaction failed', 'Wait for network congestion to clear']
  },
  SLIPPAGE_EXCEEDED: {
    code: 'SLIPPAGE_EXCEEDED',
    title: 'Slippage Too High',
    message: 'Price moved too much during the transaction.',
    suggestions: ['Try again with higher slippage tolerance', 'Wait for price to stabilize', 'Use smaller trade amounts']
  },
  
  // Trading errors
  INVALID_TOKEN: {
    code: 'INVALID_TOKEN',
    title: 'Invalid Token',
    message: 'The specified token address is not valid.',
    suggestions: ['Check the token address', 'Make sure the token exists on Solana']
  },
  TOKEN_NOT_FOUND: {
    code: 'TOKEN_NOT_FOUND',
    title: 'Token Not Found',
    message: 'Could not find information for this token.',
    suggestions: ['Check the token address', 'The token might be new or delisted']
  },
  LIQUIDITY_ERROR: {
    code: 'LIQUIDITY_ERROR',
    title: 'Insufficient Liquidity',
    message: 'Not enough liquidity available for this trade.',
    suggestions: ['Try a smaller amount', 'Wait for more liquidity', 'Check if the token is actively traded']
  },
  PRICE_IMPACT_HIGH: {
    code: 'PRICE_IMPACT_HIGH',
    title: 'High Price Impact',
    message: 'This trade will significantly affect the token price.',
    suggestions: ['Consider reducing the trade amount', 'Split the trade into smaller parts', 'Proceed with caution']
  },
  
  // API errors
  RATE_LIMITED: {
    code: 'RATE_LIMITED',
    title: 'Too Many Requests',
    message: 'You are making requests too quickly. Please slow down.',
    suggestions: ['Wait a moment before trying again', 'Reduce the frequency of your requests']
  },
  SERVER_ERROR: {
    code: 'SERVER_ERROR',
    title: 'Server Error',
    message: 'An unexpected server error occurred.',
    suggestions: ['Try again in a moment', 'Contact support if the issue persists']
  },
  VALIDATION_ERROR: {
    code: 'VALIDATION_ERROR',
    title: 'Invalid Input',
    message: 'The provided information is not valid.',
    suggestions: ['Check your input and try again', 'Make sure all required fields are filled']
  },
  
  // Generic fallback
  UNKNOWN_ERROR: {
    code: 'UNKNOWN_ERROR',
    title: 'Unknown Error',
    message: 'An unexpected error occurred.',
    suggestions: ['Try again', 'Refresh the page', 'Contact support if the issue persists']
  }
};

/**
 * Parse error from various sources and return standardized error object
 */
export const parseError = (error) => {
  // If it's already a parsed error object
  if (error?.code && ERROR_CODES[error.code]) {
    return {
      ...ERROR_CODES[error.code],
      originalError: error
    };
  }

  // API response error
  if (error?.response?.data?.error) {
    const apiError = error.response.data.error;
    const errorCode = apiError.code || 'SERVER_ERROR';
    
    if (ERROR_CODES[errorCode]) {
      return {
        ...ERROR_CODES[errorCode],
        message: apiError.message || ERROR_CODES[errorCode].message,
        details: apiError.details,
        originalError: error
      };
    }
  }

  // Network errors
  if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
    return {
      ...ERROR_CODES.NETWORK_ERROR,
      originalError: error
    };
  }

  // Timeout errors
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return {
      ...ERROR_CODES.TIMEOUT_ERROR,
      originalError: error
    };
  }

  // Wallet errors
  if (error?.message?.includes('User rejected') || error?.code === 4001) {
    return {
      code: 'USER_REJECTED',
      title: 'Transaction Cancelled',
      message: 'You cancelled the transaction.',
      suggestions: ['Try again if you want to complete the transaction'],
      originalError: error
    };
  }

  // Solana transaction errors
  if (error?.message?.includes('insufficient funds')) {
    return {
      ...ERROR_CODES.INSUFFICIENT_BALANCE,
      originalError: error
    };
  }

  if (error?.message?.includes('slippage')) {
    return {
      ...ERROR_CODES.SLIPPAGE_EXCEEDED,
      originalError: error
    };
  }

  // Default to unknown error
  return {
    ...ERROR_CODES.UNKNOWN_ERROR,
    message: error?.message || ERROR_CODES.UNKNOWN_ERROR.message,
    originalError: error
  };
};

/**
 * Format error for display
 */
export const formatErrorForDisplay = (error) => {
  const parsedError = parseError(error);
  
  return {
    title: parsedError.title,
    message: parsedError.message,
    suggestions: parsedError.suggestions,
    code: parsedError.code,
    canRetry: isRetryableError(parsedError.code),
    severity: getErrorSeverity(parsedError.code)
  };
};

/**
 * Check if an error is retryable
 */
export const isRetryableError = (errorCode) => {
  const retryableErrors = [
    'NETWORK_ERROR',
    'TIMEOUT_ERROR',
    'TRANSACTION_TIMEOUT',
    'SERVER_ERROR',
    'RATE_LIMITED'
  ];
  
  return retryableErrors.includes(errorCode);
};

/**
 * Get error severity level
 */
export const getErrorSeverity = (errorCode) => {
  const highSeverityErrors = [
    'INSUFFICIENT_BALANCE',
    'WALLET_CONNECTION_FAILED',
    'TRANSACTION_FAILED'
  ];
  
  const mediumSeverityErrors = [
    'SLIPPAGE_EXCEEDED',
    'PRICE_IMPACT_HIGH',
    'LIQUIDITY_ERROR'
  ];
  
  if (highSeverityErrors.includes(errorCode)) return 'high';
  if (mediumSeverityErrors.includes(errorCode)) return 'medium';
  return 'low';
};

/**
 * Log error for debugging
 */
export const logError = (error, context = {}) => {
  const parsedError = parseError(error);
  
  console.error('Error occurred:', {
    code: parsedError.code,
    message: parsedError.message,
    context,
    originalError: parsedError.originalError,
    timestamp: new Date().toISOString()
  });
  
  // In production, you might want to send this to an error tracking service
  if (process.env.NODE_ENV === 'production') {
    // Example: Sentry, LogRocket, etc.
    // errorTrackingService.captureError(parsedError, context);
  }
};